/**
 * Realtime fan-out core (TO-468, ADR-0049). A plain module store: consumers
 * subscribe here and never touch MQTT. Holds the connection state (observable
 * through a hook) and a session log of the latest messages for diagnostics.
 */
import type { RealtimeMessage } from '@today/types'
import { useSyncExternalStore } from 'react'

export enum RealtimeConnectionState {
	/** Realtime is not configured for this build — polling only. */
	DISABLED = 'DISABLED',
	/** Not started, or stopped. */
	IDLE = 'IDLE',
	CONNECTING = 'CONNECTING',
	/** Subscribed with a SUBACK < 128. */
	CONNECTED = 'CONNECTED',
	/** Waiting for a backoff timer before the next attempt. */
	RECONNECTING = 'RECONNECTING',
}

/** Every present field must equal the message's; an empty filter matches all. */
export interface RealtimeFilter {
	type?: string
	entityType?: string
	entityId?: string
}

export type RealtimeHandler = (message: RealtimeMessage) => void

/** How many received messages the session log keeps. */
export const REALTIME_MESSAGE_LOG_SIZE = 20

interface Subscriber {
	filter: RealtimeFilter
	handler: RealtimeHandler
}

/** Where a failing handler is reported (the browser console; the app has no client logger). */
const log: Pick<Console, 'error'> = console

let subscribers: Subscriber[] = []
let state = RealtimeConnectionState.IDLE
let stateListeners: Array<(state: RealtimeConnectionState) => void> = []
let messageLog: readonly RealtimeMessage[] = []
let logListeners: Array<() => void> = []

const matches = ({ type, entityType, entityId }: RealtimeFilter, message: RealtimeMessage): boolean =>
	(type === undefined || type === message.type) &&
	(entityType === undefined || entityType === message.entityRef.type) &&
	(entityId === undefined || entityId === message.entityRef.id)

/** Register a handler; returns its unsubscribe. */
export function subscribeRealtime(filter: RealtimeFilter, handler: RealtimeHandler): () => void {
	const subscriber = { filter, handler }
	subscribers = [...subscribers, subscriber]
	return () => {
		subscribers = subscribers.filter((s) => s !== subscriber)
	}
}

/**
 * Fan a message out to every matching handler. Iterates a snapshot (a handler
 * may unsubscribe mid-dispatch), and one throwing handler never robs the others.
 */
export function dispatchRealtimeMessage(message: RealtimeMessage): void {
	messageLog = [message, ...messageLog].slice(0, REALTIME_MESSAGE_LOG_SIZE)
	for (const listener of logListeners) listener()
	for (const { filter, handler } of subscribers) {
		if (!matches(filter, message)) continue
		try {
			handler(message)
		} catch (error) {
			log.error('realtime handler failed', error)
		}
	}
}

/** The latest received messages, newest first. */
export const getRealtimeMessageLog = (): readonly RealtimeMessage[] => messageLog

export const getRealtimeConnectionState = (): RealtimeConnectionState => state

/** Set the connection state; listeners hear changes only. */
export function setRealtimeConnectionState(next: RealtimeConnectionState): void {
	if (next === state) return
	state = next
	for (const listener of stateListeners) listener(next)
}

export function subscribeRealtimeConnectionState(listener: (state: RealtimeConnectionState) => void): () => void {
	stateListeners = [...stateListeners, listener]
	return () => {
		stateListeners = stateListeners.filter((l) => l !== listener)
	}
}

const subscribeLog = (listener: () => void): (() => void) => {
	logListeners = [...logListeners, listener]
	return () => {
		logListeners = logListeners.filter((l) => l !== listener)
	}
}

/** The live connection state. */
export const useRealtimeConnectionState = (): RealtimeConnectionState =>
	useSyncExternalStore(subscribeRealtimeConnectionState, getRealtimeConnectionState)

/** The live session message log. */
export const useRealtimeMessageLog = (): readonly RealtimeMessage[] =>
	useSyncExternalStore(subscribeLog, getRealtimeMessageLog)

/** Test helper: back to an empty, IDLE store. */
export function resetRealtimeDispatcher(): void {
	subscribers = []
	stateListeners = []
	messageLog = []
	logListeners = []
	state = RealtimeConnectionState.IDLE
}
